import { Suspense, use, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useLoaderData, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { forgetGoogleAccount } from '../lib/googleIdentity';
import '../styles/auth.css';

interface SignOutLoaderData {
  done: Promise<void>;
}

const MIN_SCENE_MS = 2400;
const NAV_STORAGE_KEY = 'cinema-studio-nav';

async function performSignOut(): Promise<void> {
  await Promise.all([
    api.logout().catch(() => undefined),
    new Promise(resolve => setTimeout(resolve, MIN_SCENE_MS))
  ]);
  forgetGoogleAccount();
  try {
    localStorage.removeItem(NAV_STORAGE_KEY);
  } catch {
    return;
  }
}

export function signOutLoader(): SignOutLoaderData {
  return { done: performSignOut() };
}

function Finished({ done }: SignOutLoaderData) {
  use(done);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setTimeout(() => navigate('/signin?signedout=1', { replace: true }), 650);
    return () => window.clearTimeout(timer);
  }, [navigate]);

  return <Steps finished />;
}

function Steps({ finished }: { finished: boolean }) {
  return (
    <ul className="sa-steps" data-done={finished || undefined}>
      {['Closing studio session', 'Forgetting Google auto sign-in', 'Clearing this device'].map((step, index) => (
        <li key={step} style={{ '--i': index } as CSSProperties}>
          {step}
        </li>
      ))}
    </ul>
  );
}

export default function SignOut() {
  const { done } = useLoaderData() as SignOutLoaderData;

  return (
    <main className="sa-wrap" role="status" aria-live="polite">
      <div className="sa-wrap-inner">
        <div className="sa-clapper" aria-hidden="true">
          <div className="sa-clapper-arm" />
          <div className="sa-clapper-board">
            SCENE · WRAP
            <br />
            TAKE · FINAL
          </div>
        </div>
        <h1>
          <span className="sa-mask" style={{ '--i': 0 } as CSSProperties}>
            <span>Cut.</span>
          </span>
          <span className="sa-mask" style={{ '--i': 1 } as CSSProperties}>
            <span>That’s a wrap.</span>
          </span>
        </h1>
        <p>Signing you out of the studio on every device.</p>
        <Suspense fallback={<Steps finished={false} />}>
          <Finished done={done} />
        </Suspense>
      </div>
    </main>
  );
}
